import { Button, Dropdown, FormLayout, TextInput } from 'components/common'
import { DynamicInputList } from 'components/common/dynamicInputList'
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form'
import { CreateProductFormFields } from 'utils/schema/bundleProductSchema'
import {
  getDepartmentOptions,
  getFamiliesOptionsBySection,
  getSectionOptionsByDepartment,
} from 'utils/helpers'
import { VAT_CLASS_OPTIONS } from 'utils/constants'
import AggregatorsDropdown from 'components/masterData/aggregatorsDropdown'
import BundleImageField from './bundleImageField'
import BundleItemsFields from './bundleItemsFields'
import BundlePricingFields from './bundlePricingFields'
import SwitchField from './switchField'

type Props = {
  register: UseFormRegister<CreateProductFormFields>
  control: Control<CreateProductFormFields>
  setValue: UseFormSetValue<CreateProductFormFields>
  errors: FieldErrors<CreateProductFormFields>
}

const CreateBundleProductForm = ({
  register,
  control,
  setValue,
  errors,
}: Props) => {
  const dept = useWatch({ control, name: 'dept' })
  const section = useWatch({ control, name: 'section' })

  return (
    <>
      <h3 className="title mb-4 mt-4">Product Details</h3>
      <FormLayout>
        <BundleImageField
          label="Product Image"
          name="image"
          control={control}
          error={errors?.image?.message}
        />

        <div className="mt-5 grid gap-5 sm:grid-cols-2 md:grid-cols-3">
          <TextInput
            label="Description"
            name="description"
            register={register}
            required
            error={errors?.description?.message}
          />

          <TextInput
            label="Online Description"
            name="onlineDesc"
            register={register}
            error={errors?.onlineDesc?.message}
          />

          <Dropdown
            label="Department"
            name="dept"
            setValue={setValue}
            required
            options={getDepartmentOptions()}
            error={errors?.dept?.message}
          />

          <Dropdown
            label="Section"
            name="section"
            setValue={setValue}
            required
            options={getSectionOptionsByDepartment(dept)}
            error={errors?.section?.message}
          />

          <Dropdown
            label="Family"
            name="family"
            setValue={setValue}
            required
            options={getFamiliesOptionsBySection(section)}
            error={errors?.family?.message}
          />

          <TextInput
            label="Sub Family"
            name="subFamily"
            register={register}
            required
            error={errors?.subFamily?.message}
          />

          <TextInput
            label="UOM"
            name="uom"
            register={register}
            required
            error={errors?.uom?.message}
          />

          <Dropdown
            label="VAT Class"
            name="vatClass"
            setValue={setValue}
            required
            options={VAT_CLASS_OPTIONS}
            error={errors?.vatClass?.message}
          />

          <TextInput
            label="Qty Step"
            name="quantityStep"
            type="number"
            step="any"
            register={register}
            error={errors?.quantityStep?.message}
          />
        </div>

        <div className="mt-5 grid gap-x-8 divide-y divide-gray-100 md:grid-cols-2">
          <SwitchField
            control={control}
            name="activeOnline"
            label="Active Online"
            description="Activates this product on our portal"
            defaultValue
          />
          <SwitchField
            control={control}
            name="isDisable"
            label="Is Disable"
            description="Treats this product as out of stock"
          />
        </div>
      </FormLayout>

      <h3 className="title">Bundle Pricing</h3>
      <FormLayout>
        <BundlePricingFields register={register} errors={errors} />
      </FormLayout>

      <h3 className="title">Products</h3>
      <FormLayout>
        <BundleItemsFields
          control={control}
          register={register}
          errors={errors}
        />
      </FormLayout>

      <h3 className="title">Additional Details</h3>
      <FormLayout>
        <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3">
          <TextInput
            label="ERP Brand"
            name="erpBrand"
            register={register}
            error={errors?.erpBrand?.message}
          />

          <TextInput
            label="ERP Country of Origin"
            name="erpCoo"
            register={register}
            error={errors?.erpCoo?.message}
          />

          <TextInput
            label="ERP Marketing Code"
            name="erpMarketingCode"
            register={register}
            error={errors?.erpMarketingCode?.message}
          />

          <TextInput
            label="Online Pack Info"
            name="onlinePackInfo"
            register={register}
            error={errors?.onlinePackInfo?.message}
          />

          <TextInput
            label="UOM Packaging Info"
            name="uomPackagingInfo"
            register={register}
            error={errors?.uomPackagingInfo?.message}
          />

          <TextInput
            label="Qty Per UOM"
            name="qtyPerUom"
            register={register}
            error={errors?.qtyPerUom?.message}
          />

          <AggregatorsDropdown
            setValue={setValue}
            resetForm={false}
            name="hideAggregators"
            label="Hide Aggregators"
            error={errors?.hideAggregators?.message as string}
            isActive
          />

          <DynamicInputList
            setValue={setValue}
            name="specialCategories"
            label="Special Categories"
            placeholder="Add special category"
            errors={errors}
          />

          <DynamicInputList
            setValue={setValue}
            name="classifications"
            label="Classifications"
            placeholder="Add classification"
            errors={errors}
          />
        </div>
      </FormLayout>

      <Button
        buttonText="Create Bundle Product"
        className="mt-7 w-56"
        type="submit"
      />
    </>
  )
}

export default CreateBundleProductForm
